import { IAreaDimension, IDimensionPoint, ILinearDimension, updateAreaDimension } from '../data/dimension/dimension';
import { modelManager } from '../manager/entityManager';
import { IPoint2d } from '../math/IPoint2d';
import { TGlobalStores } from '../stores';
import { EOperationType } from '../types/operation';

export class DimensionOperation {
  private _dimensionStore: TGlobalStores['dimensionStore'];
  private _history: TGlobalStores['history'];

  constructor(gs: TGlobalStores) {
    this._dimensionStore = gs.dimensionStore;
    this._history = gs.history;
  }

  private $history_addAreaDimension(areaDimension: IAreaDimension) {
    this._history.startOperation();
    this._dimensionStore.addAreaDim(areaDimension);
    this._history.completeOperation(EOperationType.AreaDimensionAdd);
  }

  private $history_addLinearDimension(linearDimension: ILinearDimension) {
    this._history.startOperation();
    this._dimensionStore.addLinearDim(linearDimension);
    this._history.completeOperation(EOperationType.LinearDimensionAdd);
  }

  private $history_modifyAreaDimension(areaDimension: IAreaDimension) {
    this._history.startOperation();
    this._dimensionStore.updateAreaDim(areaDimension);
    this._history.completeOperation(EOperationType.AreaDimensionAdd);
  }

  private $history_modifyLinearDimension(linearDimension: ILinearDimension) {
    this._history.startOperation();
    this._dimensionStore.updateLinearDim(linearDimension.id, linearDimension);
    this._history.completeOperation(EOperationType.LinearDimensionModify);
  }

  private $history_deleteAreaDimension(areaDimensionId: string) {
    this._history.startOperation();
    this._dimensionStore.removeAreaDim(areaDimensionId);
    this._history.completeOperation(EOperationType.AreaDimensionDel);
  }

  private $history_deleteLinearDimension(linearDimensionId: string) {
    this._history.startOperation();
    this._dimensionStore.removeLinearDim(linearDimensionId);
    this._history.completeOperation(EOperationType.LinearDimensionDel);
  }

  addAreaDimension(areaDimension: IAreaDimension) {
    this.$history_addAreaDimension(areaDimension);
    this._refresh();
  }

  deleteAreaDimension(areaDimensionId: string) {
    if (!this._dimensionStore.getAreaDim(areaDimensionId)) return false;

    this.$history_deleteAreaDimension(areaDimensionId);
    this._refresh();
    return true;
  }

  modifyAreaDimension(areaDimensionId: string, polygon: IDimensionPoint[]) {
    const data = this._dimensionStore.getAreaDim(areaDimensionId);
    if (data) {
      this.$history_modifyAreaDimension({ ...data, ...updateAreaDimension(polygon) });
    }
    this._refresh();
    modelManager.transformManager.updateAreaTransform(polygon);
  }

  addEndpoint(point: IPoint2d, theme: 'normal' | 'highlight' = 'normal') {
    if (theme === 'highlight') modelManager.dimensionManager.addHighlightEndPoint(point);
    else modelManager.dimensionManager.addEndPoint(point);
  }

  removeEndPoint(point: IPoint2d) {
    modelManager.dimensionManager.removeEndPoint(point);
  }

  removeAllEndPoints() {
    modelManager.dimensionManager.removeAllEndPoints();
  }

  addLinearDimension(linearDimension: ILinearDimension) {
    this.$history_addLinearDimension(linearDimension);
    this._refresh();
  }

  deleteLinearDimension(id: string) {
    if (!this._dimensionStore.getLinearDim(id)) return false;

    this.$history_deleteLinearDimension(id);
    this._refresh();
    return true;
  }

  modifyLinearDimension(linearDimension: ILinearDimension) {
    if (!this._dimensionStore.getLinearDim(linearDimension.id)) return;

    this.$history_modifyLinearDimension(linearDimension);
    this._refresh();
  }

  private _refresh() {
    modelManager.dimensionManager.update();
    modelManager.textInfoManager.updateAreaDimensionTextInfos();
    modelManager.selectedRerender();
    modelManager.hoverRerender();
  }
}
